import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { appointmentsAtLocation } from '../utils/practice-locations';
import { GroupService } from './group.service';
import { LocationService } from './location.service';

/** `null` = all locations. Otherwise the id of the location the calendar and upcoming list show. */
export type CalendarLocationFilterSelection = string | null;

const STORAGE_PREFIX = 'calendarLocationFilter:';

/**
 * The location filter shared by the calendar, the upcoming list and their dialogs (tasks/practice-locations.md).
 * Remembered per group on this device; it falls back to all locations when the group has none or the remembered one
 * was removed. Applied alongside the physician filter, so the two combine.
 */
@Injectable({
  providedIn: 'root',
})
export class CalendarLocationFilterService {
  private readonly selectedLocationIdSubject = new BehaviorSubject<CalendarLocationFilterSelection>(null);

  readonly selectedLocationId$: Observable<CalendarLocationFilterSelection> = this.selectedLocationIdSubject.asObservable();

  /** The group's active locations. */
  locations: any[] = [];

  constructor(
    private groupService: GroupService,
    private locationService: LocationService,
  ) {
    combineLatest([this.groupService.selectedGroup$, this.locationService.locations$]).subscribe(([group, locations]) => {
      this.locations = locations || [];
      const stored = group?.id ? this.readStored(group.id) : null;
      const valid = stored && this.locations.some((l) => l.id === stored) ? stored : null;
      if (valid !== this.selectedLocationIdSubject.getValue()) {
        this.selectedLocationIdSubject.next(valid);
      }
    });
  }

  getSelectedLocationId(): CalendarLocationFilterSelection {
    return this.selectedLocationIdSubject.getValue();
  }

  setSelectedLocationId(value: CalendarLocationFilterSelection, groupId?: string): void {
    this.selectedLocationIdSubject.next(value);
    const gid = groupId ?? this.groupService.group?.id;
    if (gid) {
      this.persist(gid, value);
    }
  }

  applyAppointmentFilter<T extends { patientId?: string; locationId?: string | null; Patient?: { id?: string } }>(
    rows: T[] | null | undefined,
    selected: CalendarLocationFilterSelection = this.getSelectedLocationId(),
  ): T[] {
    return appointmentsAtLocation(rows || [], selected, this.locations);
  }

  private readStored(groupId: string): string | null {
    try {
      return localStorage.getItem(STORAGE_PREFIX + groupId) || null;
    } catch {
      return null;
    }
  }

  private persist(groupId: string, value: CalendarLocationFilterSelection): void {
    try {
      if (value === null) {
        localStorage.removeItem(STORAGE_PREFIX + groupId);
      } else {
        localStorage.setItem(STORAGE_PREFIX + groupId, value);
      }
    } catch {
      // Storage unavailable: the choice lasts for this session only.
    }
  }
}
